const { previewEnvironment } = require('./useNerpy');
const { useEffect, useRef, useState } = require('react');
const { context } = require('app-context');
const { v4: uuid } = require('uuid');
const { default: Button } = require('@mui/material/Button');
const { default: TextField } = require('@mui/material/TextField');
const { default: Checkbox } = require('@mui/material/Checkbox');
const { default: FormControlLabel } = require('@mui/material/FormControlLabel');
const { default: Styled } = require('styled-components');

const internals = {};
const GameLibraryAdmin = require('./GameLibraryAdmin');

module.exports = function TriviaAdmin({ schoolId, currentUserId }) {

    const { Editor } = internals;
    if (!previewEnvironment() || schoolId !== 240394) {
        return null;
    }

    return <><GameLibraryAdmin key={`games:${schoolId}:${currentUserId}`} /><Editor key={`${schoolId}:${currentUserId}`} /></>;
};

internals.Editor = function Editor() {

    const [questions, setQuestions] = useState(null);
    const [draft, setDraft] = useState(null);
    const [status, setStatus] = useState('');
    const [saving, setSaving] = useState(false);
    const [games, setGames] = useState(null);
    const active = useRef(true);
    const inFlight = useRef(false);
    const { Panel, Fields, Buttons, kinds, edit, validate, payload } = internals;
    useEffect(() => {

        active.current = true;
        context.api.nearpeer.nerpy.triviaGamesAdmin?.().then((value) => { if (active.current) { setGames(value.games); } }).catch(() => {});
        context.api.nearpeer.nerpy.triviaCatalog().then((rows) => {

            if (active.current) {
                setQuestions(rows);
            }
        }).catch(() => {});
        return () => { active.current = false; };
    }, []);

    if (!questions) {
        return null;
    }

    const update = (name, value) => setDraft((current) => ({ ...current, [name]: value }));
    const choose = (question) => {

        setDraft(edit(question));
        setStatus('');
    };
    const save = async () => {

        if (inFlight.current) {
            return;
        }

        const error = validate(draft);
        if (error) {
            setStatus(error);
            return;
        }

        inFlight.current = true;
        setSaving(true);
        setStatus('');
        try {
            const saved = await context.api.nearpeer.nerpy.saveTriviaQuestion(payload(draft));
            if (active.current) {
                setQuestions((rows) => rows.some((row) => row.key === saved.key) ? rows.map((row) => row.key === saved.key ? saved : row) : [...rows, saved]);
                setDraft(edit(saved));
                setStatus('Question saved.');
            }
        }
        catch {
            if (active.current) {
                setStatus('Question was not saved. Your edits are still here. Check the fields and your access, then try again.');
            }
        }
        finally {
            if (active.current) {
                inFlight.current = false;
                setSaving(false);
            }
        }
    };

    return <Panel>
        <summary>companion trivia questions</summary>
        <p>Manage this school’s private demo questions. Saving a question does not send messages or turn on the companion.</p>
        <Fields>
            <TextField select SelectProps={{ native: true }} label='Choose a question' value={draft?.key || ''} disabled={saving} onChange={(event) => {

                const question = questions.find((row) => row.key === event.target.value);
                if (question) {
                    choose(question);
                }
            }}>
                <option value=''>Select a question</option>
                {draft && !questions.some((row) => row.key === draft.key) && <option value={draft.key}>New question</option>}
                {questions.map((question) => <option key={question.key} value={question.key}>{question.answer}{question.enabled ? '' : ' (disabled)'}</option>)}
            </TextField>
            <Buttons><Button type='button' variant='outlined' disabled={saving} onClick={() => choose(internals.empty())}>Add question</Button></Buttons>
            {draft && <>
                {games && <>
                    <TextField select SelectProps={{ native: true }} label='Game' value={draft.gameKey || ''} disabled={saving} onChange={(event) => update('gameKey', event.target.value)}><option value=''>Choose a game</option>{games.map((game) => <option key={game.key} value={game.key}>{game.title}</option>)}</TextField>
                    <TextField label='Question order' type='number' value={draft.sortOrder ?? 0} disabled={saving} onChange={(event) => update('sortOrder', Number(event.target.value))} />
                    <TextField select SelectProps={{ native: true }} label='Difficulty' value={draft.difficulty || 'medium'} disabled={saving} onChange={(event) => update('difficulty', event.target.value)}>{['easy', 'medium', 'hard', 'hardest'].map((value) => <option key={value}>{value}</option>)}</TextField>
                </>}
                <TextField select SelectProps={{ native: true }} label='Question type' value={draft.kind} disabled={saving} onChange={(event) => update('kind', event.target.value)}>
                    {Object.entries(kinds).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </TextField>
                {draft.kind.startsWith('emoji-') && <TextField label='Three emoji clue' value={draft.emojiClue || ''} disabled={saving} onChange={(event) => update('emojiClue', event.target.value)} inputProps={{ maxLength: 100 }} />}
                {draft.kind === 'art-book' && <>
                    {['url', 'alt', 'title', 'artist', 'reference', 'rightsStatus'].map((field) => <TextField key={field} fullWidth label={`Artwork ${field}`} value={draft.imageClue?.[field] || ''} disabled={saving} onChange={(event) => update('imageClue', { url: null, alt: '', title: '', artist: '', reference: null, rightsStatus: '', reviewed: false, ...draft.imageClue, [field]: event.target.value || (['url', 'reference'].includes(field) ? null : '') })} />)}
                    <FormControlLabel label='Artwork image and reproduction rights reviewed' control={<Checkbox checked={!!draft.imageClue?.reviewed} disabled={saving} onChange={(event) => update('imageClue', { ...draft.imageClue, reviewed: event.target.checked })} />} />
                </>}
                {games && <TextField multiline label='Explanation after the answer' value={draft.explanation || ''} disabled={saving} onChange={(event) => update('explanation', event.target.value)} />}
                <TextField fullWidth multiline label='Question shown to players' value={draft.prompt} disabled={saving} onChange={(event) => update('prompt', event.target.value)} inputProps={{ maxLength: 1000 }} helperText='Include the emoji clue in this text for emoji questions.' />
                <TextField fullWidth multiline label='Accessible question description' value={draft.accessiblePrompt} disabled={saving} onChange={(event) => update('accessiblePrompt', event.target.value)} inputProps={{ maxLength: 1000 }} helperText='Describe the symbols in words without giving away the answer.' />
                <TextField fullWidth label='Answer' value={draft.answer} disabled={saving} onChange={(event) => update('answer', event.target.value)} inputProps={{ maxLength: 200 }} />
                <TextField fullWidth multiline label='Other accepted answers, one per line' value={draft.aliasesText} disabled={saving} onChange={(event) => update('aliasesText', event.target.value)} />
                <TextField fullWidth multiline label='Hints, one per line' value={draft.hintsText} disabled={saving} onChange={(event) => update('hintsText', event.target.value)} helperText='Up to five hints, in the order players will see them.' />
                <TextField fullWidth label='Clue creator' value={draft.source.creator} disabled={saving} onChange={(event) => update('source', { ...draft.source, creator: event.target.value })} inputProps={{ maxLength: 100 }} />
                <TextField fullWidth label='Collection' value={draft.source.collection} disabled={saving} onChange={(event) => update('source', { ...draft.source, collection: event.target.value })} inputProps={{ maxLength: 100 }} />
                <p>Private demo only · Rights status: {draft.source.rightsStatus}. Published movie clues still require review of the contributor agreement before public use.</p>
                <FormControlLabel label='Available to players' control={<Checkbox checked={draft.enabled} disabled={saving} onChange={(event) => update('enabled', event.target.checked)} />} />
                <Buttons><Button type='button' variant='contained' disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save question'}</Button></Buttons>
            </>}
            <p role='status'>{status}</p>
        </Fields>
    </Panel>;
};

internals.kinds = { 'emoji-movie': 'Movie: three emoji', 'emoji-tv': 'TV show: three emoji', 'emoji-band': 'Band: three emoji', 'emoji-song': 'Song: three emoji', trivia: 'General trivia', 'art-book': 'Artwork and book title' };
internals.empty = () => ({ key: `question-${uuid()}`, kind: 'emoji-movie', emojiClue: '', prompt: '', accessiblePrompt: '', answer: '', aliases: [], hints: [], enabled: false, source: { creator: '', collection: '', usage: 'private-demo', rightsStatus: 'author-review-pending' } });
internals.edit = (question) => ({ ...question, source: { ...question.source }, aliasesText: question.aliases.join('\n'), hintsText: question.hints.join('\n') });
internals.lines = (text) => text.split('\n').map((line) => line.trim()).filter(Boolean);
internals.payload = (draft) => ({ key: draft.key, kind: draft.kind, emojiClue: draft.kind.startsWith('emoji-') ? (draft.emojiClue || '').trim() : null, prompt: draft.prompt.trim(), accessiblePrompt: draft.accessiblePrompt.trim(), answer: draft.answer.trim(), aliases: internals.lines(draft.aliasesText), hints: internals.lines(draft.hintsText), source: { ...draft.source, creator: draft.source.creator.trim(), collection: draft.source.collection.trim() }, enabled: draft.enabled, ...(draft.gameKey ? { gameKey: draft.gameKey, sortOrder: draft.sortOrder || 0, difficulty: draft.difficulty || 'medium', explanation: draft.explanation || null, imageClue: draft.kind === 'art-book' ? draft.imageClue : null } : {}) });
internals.validate = (draft) => {

    const q = internals.payload(draft);
    if (![q.prompt, q.accessiblePrompt, q.answer, q.source.creator, q.source.collection].every(Boolean)) {
        return 'Enter the question, accessible description, answer, creator, and collection.';
    }

    if (q.kind === 'art-book' && q.enabled && (!q.imageClue?.reviewed || !q.imageClue.url || !q.explanation)) {
        return 'Review the artwork image and add its explanation before enabling this question.';
    }

    if (q.kind.startsWith('emoji-') && (q.enabled || q.emojiClue)) {
        if (typeof Intl.Segmenter !== 'function') {
            return 'Your browser needs an update to validate emoji clues.';
        }

        const symbols = [...new Intl.Segmenter('en', { granularity: 'grapheme' }).segment(q.emojiClue.replace(/\s/gu, ''))].map((part) => part.segment);
        if (symbols.length !== 3 || !symbols.every((part) => /\p{Extended_Pictographic}/u.test(part) || /^\p{Regional_Indicator}{2}$/u.test(part) || /^[#*0-9]\uFE0F?\u20E3$/u.test(part))) {
            return 'Use exactly three emoji for the clue.';
        }
    }

    if (q.aliases.length > 20 || q.aliases.some((line) => line.length > 200) || q.hints.length > 5 || q.hints.some((line) => line.length > 300)) {
        return 'Use up to 20 alternate answers (200 characters each) and five hints (300 characters each).';
    }

    return '';
};

internals.Panel = Styled.details`padding: 16px; margin-bottom: 24px; border: 1px solid ${({ theme }) => theme.palette?.divider || '#ddd'}; border-radius: 8px; summary { cursor: pointer; font-weight: bold; }`;
internals.Fields = Styled.div`display: flex; flex-direction: column; gap: 12px;`;
internals.Buttons = Styled.div`display: flex; flex-wrap: wrap; gap: 8px;`;
